import React, { useState, useEffect } from "react";
import { t } from "i18next";
import { Button, Input, Label, Spinner, Card, CardBody } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useOptionStore } from "../../../stores/Assets/ItemOptions";

const OptionForm = ({
  itemId,
  setIsFormVisible,
  selectedOption,
  onFormClose,
}) => {
  const [creating, setCreating] = useState(false);
  const [defaultChoice, setDefaultChoice] = useState(null);
  const [editingOptionId, setEditingOptionId] = useState(null);

  const { createOption, updateOption } = useOptionStore((state) => state);

  const getInitialChoices = () => {
    if (selectedOption?.choices?.length > 0) {
      return selectedOption.choices.map((choice) => ({
        id: choice.menu_item_option_choice_id || choice.id || Date.now(),
        name: choice.name || "",
        name_ar: choice.name_ar || "",
        price: choice.price || 0,
        available: choice.available !== undefined ? choice.available : true,
      }));
    }
    return [
      { id: 1, name: "", name_ar: "", price: 0, available: true },
      { id: 2, name: "", name_ar: "", price: 0, available: true },
    ];
  };

  useEffect(() => {
    if (selectedOption) {
      setEditingOptionId(selectedOption.menu_item_option_id || selectedOption.id);

      if (selectedOption.default_choice) {
        setDefaultChoice({
          id: selectedOption.default_choice.menu_item_option_choice_id,
          name: selectedOption.default_choice.name,
          name_ar: selectedOption.default_choice.name_ar,
          available:
            selectedOption.default_choice.available !== undefined
              ? selectedOption.default_choice.available
              : true,
        });
      }
    }
  }, [selectedOption]);

  const formik = useFormik({
    initialValues: {
      name: selectedOption?.name || "",
      name_ar: selectedOption?.name_ar || "",
      name_fr: selectedOption?.name_fr || "",
      choices: getInitialChoices(),
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Option name is required"),
      name_ar: Yup.string().optional(),
      name_fr: Yup.string().optional(),
      choices: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().required("Choice name is required"),
            name_ar: Yup.string().optional(),
            price: Yup.number().min(0, "Price must be 0 or greater").optional(),
            available: Yup.boolean().optional(),
          })
        )
        .min(2, "At least two choices are required"),
    }),
    onSubmit: async (values) => {
      setCreating(true);

      if (!defaultChoice) {
        toast.error("Select a default choice.");
        setCreating(false);
        return;
      }

      try {
        // Timestamp IDs from Date.now() are > 1e12, database IDs are much smaller
        const isNewChoice = (id) => typeof id === 'number' && id > 1000000000000;
        
        const formattedOption = {
          name: values.name,
          name_ar: values.name_ar,
          name_fr: values.name_fr,
          choices: values.choices.map((choice) => ({
            // Use menu_item_option_choice_id for existing choices, null for new ones
            menu_item_option_choice_id: isNewChoice(choice.id) ? null : choice.id,
            name: choice.name,
            name_ar: choice.name_ar,
            price: parseFloat(choice.price) || 0,
            available: choice.available !== undefined ? choice.available : true,
          })),
          default_choice: {
            menu_item_option_choice_id: isNewChoice(defaultChoice.id) ? null : defaultChoice.id,
            name: defaultChoice.name,
            name_ar: defaultChoice.name_ar,
            available:
              defaultChoice.available !== undefined
                ? defaultChoice.available
                : true,
          },
        };

        if (editingOptionId) {
          await updateOption(itemId, formattedOption, editingOptionId);
        } else {
          await createOption(itemId, formattedOption);
        }

        setEditingOptionId(null);
        handleCloseForm();
      } catch (error) {
        console.error("Failed to create or update option", error);
      } finally {
        setCreating(false);
      }
    },
  });

  const handleCloseForm = () => {
    setIsFormVisible(false);
    setEditingOptionId(null);
    setDefaultChoice(null);
    formik.resetForm();
    onFormClose?.();
  };

  useEffect(() => {
    // Only auto-set default choice when creating new option (not editing)
    if (!selectedOption && formik.values.choices.length > 0 && !defaultChoice) {
      setDefaultChoice(formik.values.choices[0]);
    }
  }, [formik.values.choices, selectedOption, defaultChoice]);

  const handleAddChoice = () => {
    const newChoice = { id: Date.now(), name: "", name_ar: "", price: 0, available: true };
    formik.setFieldValue("choices", [...formik.values.choices, newChoice]);
  };

  const handleRemoveChoice = (index) => {
    if (formik.values.choices.length <= 2) return;
    const updatedChoices = formik.values.choices.filter((_, i) => i !== index);

    formik.setFieldValue("choices", updatedChoices);

    if (defaultChoice?.id === formik.values.choices[index].id) {
      setDefaultChoice(updatedChoices.length > 0 ? updatedChoices[0] : null);
    }
  };

  const isAddDisabled = formik.values.choices.some(
    (choice) => choice.name.trim() === ""
  );

  return (
    <Card className="shadow-lg">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {editingOptionId ? t("Edit Option") : t("Add New Option")}
          </h5>
          <Button color="secondary" outline onClick={handleCloseForm}>
            {editingOptionId ? t("Cancel") : t("Add Existing")}
          </Button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex gap-3 mb-3">
            <div style={{ flex: 1 }}>
              <Label htmlFor="name">{t("Title")}</Label>
              <Input
                type="text"
                id="name"
                name="name"
                {...formik.getFieldProps("name")}
                placeholder={t("Ex. Sugar")}
                invalid={formik.touched.name && Boolean(formik.errors.name)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label htmlFor="name_ar">{t("Title (AR)")}</Label>
              <Input
                type="text"
                id="name_ar"
                name="name_ar"
                {...formik.getFieldProps("name_ar")}
                placeholder={t("Ex. السكر")}
                invalid={
                  formik.touched.name_ar && Boolean(formik.errors.name_ar)
                }
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label htmlFor="name_fr">{t("Title (FR)")}</Label>
              <Input
                type="text"
                id="name_fr"
                name="name_fr"
                {...formik.getFieldProps("name_fr")}
                placeholder={t("Ex. Sucre")}
                invalid={
                  formik.touched.name_fr && Boolean(formik.errors.name_fr)
                }
              />
            </div>
          </div>
          {/* Choices Section */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="mb-0">{t("Choices")}</Label>
            <Button
              color="primary"
              size="sm"
              outline
              onClick={handleAddChoice}
              disabled={isAddDisabled}
            >
              {t("Add Choice")}
            </Button>
          </div>

          <div className="border p-3 rounded mb-3 bg-light">
            {/* Column Headers */}
            <div className="d-flex w-100 align-items-center gap-2 mb-2 fw-bold text-muted">
              <div style={{ width: "25%" }}>{t("Choice Name")}</div>
              <div style={{ width: "25%" }}>{t("Choice Name (AR)")}</div>
              <div style={{ width: "15%" }}>{t("Price")}</div>
              
              <div style={{ width: "15%" }}>{t("Default")}</div>
              <div style={{ width: "10%" }}>{t("Available")}</div>
              <div style={{ width: "5%" }}></div>
            </div>
            {formik.values.choices.map((choice, index) => (
              <div
                key={choice.id}
                className="d-flex w-100 align-items-center gap-2 mb-2"
              >
                <Input
                  type="text"
                  value={choice.name}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `choices.${index}.name`,
                      e.target.value
                    )
                  }
                  placeholder={t("Ex. Yes")}
                  style={{ width: "25%" }}
                />

                <Input
                  type="text"
                  value={choice.name_ar}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `choices.${index}.name_ar`,
                      e.target.value
                    )
                  }
                  placeholder={t("Ex. نعم")}
                  style={{ width: "25%" }}
                />

                <Input
                  type="number"
                  value={choice.price}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `choices.${index}.price`,
                      e.target.value
                    )
                  }
                  placeholder={t("Price")}
                  step="0.01"
                  min="0"
                  style={{ width: "15%" }}
                />



                <Button
                  color={defaultChoice?.id === choice.id ? "success" : "info"}
                  size="sm"
                  outline
                  className="text-nowrap px-2"
                  style={{ width: "15%" }}
                  onClick={() => setDefaultChoice(choice)}
                >
                  {defaultChoice?.id === choice.id
                    ? t("Default")
                    : t("Set Default")}
                </Button>
                <div className="form-check form-switch" style={{ width: "10%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={choice.available !== false}
                    onChange={(e) =>
                      formik.setFieldValue(
                        `choices.${index}.available`,
                        e.target.checked
                      )
                    }
                    style={{ cursor: "pointer", width: "2.5em", height: "1.25em" }}
                  />
                </div>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleRemoveChoice(index)}
                  disabled={formik.values.choices.length <= 2}
                  style={{ width: "5%" }}
                >
                  <i className="ri-subtract-line"></i>
                </Button>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="d-flex justify-content-end">
            <Button type="submit" color="success" disabled={creating}>
              {creating ? <Spinner size="sm" color="light" /> : t("Save")}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default OptionForm;
